import { Component, Vue, Watch } from "vue-property-decorator";
import {
	Row,
	Col,
	TimePicker,
	Checkbox,
	Table,
	Modal,
	Button,
	Icon,
	Select,
	Radio,
	Form,
	InputNumber
} from "ant-design-vue";
import SumRadioGroup from "@/components/sum-radio-group/sum-radio-group.vue";
import DurationSelectRange from "@/components/content-exposure/duration-select-range/duration-select-range.vue";
import MethodologyCheckGroup from "@/components/content-exposure/methodology-check-group/methodology-check-group.vue";
import MethodologyRadioGroup from "@/components/content-exposure/methodology-radio-group/methodology-radio-group.vue";
import ContentExposureObjective from "@/components/content-exposure/content-exposure-objective/content-exposure-objective.vue";
import ContentExposureActivity from "@/components/content-exposure/content-exposure-activity/content-exposure-activity.vue";
import ContentExposureRelatedItem from "@/components/content-exposure/content-exposure-relatedItem/content-exposure-relatedItem.vue";
import ContentExposureAction from "@/components/content-exposure/content-exposure-action/content-exposure-action.vue";
import ContentExposureConversation from "@/components/content-exposure/content-exposure-conversation/content-exposure-conversation.vue";
import SlotRadioGroup from "@/components/content-exposure/slot-radio-group/slot-radio-group.vue";
import Spinning from "@/components/spinning/spinning.vue";
import { AccessLevel, BatchAction, LanguageSkill, MethodologyType, MethodologyTypeEnum, ParentType } from "@/util/enum";
import { ContentExposureModule } from "@/modules/content-exposure";
import { fmtMsg, MessageHelper, NotificationType } from "commonui";
import { CommonLocale, ContentExposureLocale, MethodologyLocale } from "@/locales/localeid";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import { isNull, isNumber, isUndefined, cloneDeep } from "lodash";
import { ContentExposureModel, TeachingActivityModel } from "@/services/teaching-activity";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import { ILessonPlanVersionService, LessonPlanVersionService } from "@/services/lesson-plan";
import { VersionGroupModule } from "@/modules/versiongroup";
import { ILessonPlanVersionSimpleModel } from "@/models/lesson-plan";
import { Methodology, MethodologyTypeItems } from "@/util/constants";
import ManageOrderDrawer from "@/components/content-exposure/manage-order-drawer/manage-order-drawer.vue";

const CheckboxGroup = Checkbox.Group;

enum AddDuplicateType {
	Add = 1,
	Duplicate = 2
}
@Component({
	components: {
		Row,
		Col,
		TimePicker,
		CheckboxGroup,
		Checkbox,
		SumRadioGroup,
		DurationSelectRange,
		MethodologyCheckGroup,
		MethodologyRadioGroup,
		ContentExposureObjective,
		ContentExposureActivity,
		ContentExposureRelatedItem,
		ContentExposureAction,
		Spinning,
		Table,
		Button,
		Icon,
		ContentExposureConversation,
		Select,
		Option: Select.Option,
		Modal,
		Radio,
		RadioGroup: Radio.Group,
		Form,
		Item: Form.Item,
		InputNumber,
		SlotRadioGroup,
		ManageOrderDrawer
	}
})
export default class ContentExposureList extends Vue {
	public columns: any = [
		{
			title: fmtMsg(ContentExposureLocale.ContentExposureDuration),
			dataIndex: "duration",
			key: "duration",
			width: "20%",
			scopedSlots: { customRender: "duration" }
		},
		{
			title: fmtMsg(ContentExposureLocale.ContentExposureRelatedItems),
			dataIndex: "relatedItems",
			key: "relatedItems",
			class: "tt-contentexposure__related-items",
			scopedSlots: { customRender: "relatedItems" },
			width: "20%"
		},
		{
			title: fmtMsg(ContentExposureLocale.ContentExposureTeachingActivity),
			dataIndex: "teachingActivities",
			key: "teachingActivities",
			scopedSlots: { customRender: "teachingActivities" },
			width: "40%"
		},
		{
			title: fmtMsg(ContentExposureLocale.ContentExposureObjective),
			dataIndex: "objectives",
			key: "objectives",
			scopedSlots: { customRender: "objectives" },
			width: "25%"
		},
		{
			title: "",
			dataIndex: "action",
			key: "action",
			width: 75,
			align: "center",
			customCell: () => ({
				class: "tt-contentexposure__body-col-action"
			}),
			scopedSlots: { customRender: "action" }
		},
		{
			title: "",
			dataIndex: "exposureConversation",
			key: "exposureConversation",
			width: 50,
			align: "center",
			customCell: () => ({
				class: "tt-contentexposure__body-col-action"
			}),
			scopedSlots: { customRender: "exposureConversation" }
		}
	];

	public columnsWithSlot: any = this.columns.map((c) => c);
	public loading = false;
	public isAddVisible = false;
	public exposureToAdd = 1;
	public commonExposureAddForm;
	public commonExposureDuplicateForm;
	public titleListModule = getModule(TitleListState);
	public recordInEditMode: string | null = null;
	public currentSequence: number = 1;
	public currentOperationType = AddDuplicateType.Add;
	public lessonPlanVersions: ILessonPlanVersionSimpleModel[] = [];
	public filteredLPVs: ILessonPlanVersionSimpleModel[] = [];
	public selectedLPVId: string = "";
	public selectedLPV: ILessonPlanVersionSimpleModel = {
		id: "",
		name: "",
		description: "",
		methodology: -1,
		status: -1,
		versionGroupId: "",
		combinedNameMethodology: ""
	};
	lessonPlanVersionService: ILessonPlanVersionService = LessonPlanVersionService();
	public duplicateTargetLPVId: string = "";
	public targetDuplicateLPVs: ILessonPlanVersionSimpleModel[] = [];
	public exposureLoading: boolean = false;
	public manageOrderDrawerVisible: boolean = false;
	public currentSelectedExposureId: string | null = null;

	created() {
		this.commonExposureAddForm = this.$form.createForm(this, { name: "commonExposureAddForm" });
		this.commonExposureDuplicateForm = this.$form.createForm(this, { name: "commonExposureDuplicateForm" });
	}

	async mounted() {
		const selectedVersionGroups = [ContentExposureModule.getVersionGroupId];
		const methodologyTypes = [
			MethodologyTypeEnum.Classic,
			MethodologyTypeEnum.Digital,
			MethodologyTypeEnum.Independent,
			MethodologyTypeEnum.Remote
		];
		this.lessonPlanVersions = await this.lessonPlanVersionService.getLessonPlanVersionsByTypes(
			selectedVersionGroups,
			methodologyTypes
		);

		this.lessonPlanVersions.forEach((lpv: ILessonPlanVersionSimpleModel) => {
			lpv.combinedNameMethodology = `${lpv.name}(${this.getMethodologyName(lpv.methodology)})`;
		});

		if (this.lessonPlanVersions.length) {
			this.selectedLPV = this.lessonPlanVersions[0];
			this.selectedLPVId = this.lessonPlanVersions[0].id;
			this.filteredLPVs = this.lessonPlanVersions;

			this.columnsWithSlot.splice(1, 0, {
				title: fmtMsg(ContentExposureLocale.ContentExposureSlot),
				dataIndex: "mediaSlots",
				key: "mediaSlots",
				width: "20%",
				scopedSlots: { customRender: "mediaSlots" }
			});

			//remove extra column
			this.columnsWithSlot.splice(6, 1);
		}

		if (this.lessonPlanVersions.length > 1) {
			this.targetDuplicateLPVs = this.lessonPlanVersions.filter((lpv) => lpv.id !== this.selectedLPV.id);
			this.duplicateTargetLPVId = this.targetDuplicateLPVs[0].id;
		}
	}

	@Watch("lessonPlanVersions")
	updateFilteredLPVs() {
		this.filteredLPVs = cloneDeep(this.lessonPlanVersions);
	}

	@Watch("selectedLPV", { immediate: true })
	async setTargetDuplicateLPVs() {
		if (this.lessonPlanVersions.length > 1 && this.selectedLPV.id !== "") {
			this.targetDuplicateLPVs = this.lessonPlanVersions.filter((lpv) => lpv.id !== this.selectedLPV.id);
			this.duplicateTargetLPVId = this.targetDuplicateLPVs[0].id;
		}

		if (this.selectedLPV.id !== "") {
			this.loadContentExposures();
		}
	}

	get addDuplicateType() {
		return AddDuplicateType;
	}

	get ContentExposureModule() {
		return ContentExposureModule;
	}

	get contentExposureLocale() {
		return ContentExposureLocale;
	}

	get methodologyLocale() {
		return MethodologyLocale;
	}

	get contentExposures() {
		const exposureData = ContentExposureModule.getContentExposures;

		this.currentSequence = exposureData.length + 1;
		return exposureData;
	}

	get methodologyTypeEnum() {
		return MethodologyTypeEnum;
	}

	get isEditing() {
		return this.contentExposures.some((d) => d.isEdit);
	}

	get contentId() {
		const { editeContentSelectedChapterId, editedContentId } = this.titleListModule;
		// If the component/Title is a Parent we need to return contentId (GL-10176)
		if (this.titleListModule.isParent) {
			return editedContentId;
		} else {
			return editeContentSelectedChapterId !== "none" ? editeContentSelectedChapterId : editedContentId;
		}
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	get isSlotVisible() {
		return (
			this.selectedLPV &&
			(this.selectedLPV.methodology === MethodologyTypeEnum.Digital ||
				this.selectedLPV.methodology === MethodologyTypeEnum.Remote)
		);
	}

	getComponent() {
		return this.titleListModule.getEditedContentDetails;
	}

	@Watch("titleListModule.editeContentSelectedChapterId")
	async updateExposures() {
		ContentExposureModule.setContentId(this.contentId);
		if (this.selectedLPV.id !== "") {
			this.loadContentExposures();
		}
	}

	get exposureSequence() {
		return this.contentExposures.length + 1;
	}

	onDurationChange(value) {
		ContentExposureModule.setDuration(value);
	}
	onMethodologyChange(value) {
		ContentExposureModule.setMethodology(value);
	}
	onRadioMethodologyChange(value) {
		ContentExposureModule.setRadioMethodology(value);
	}

	get exposureAddDecorator(): any {
		return [
			"exposureAddValue",
			{
				initialValue: 1,
				rules: [
					{
						required: true,
						message: fmtMsg(ContentExposureLocale.ContentExposureAddExposureRequiredNumberofExposure)
					},
					{ validator: this.validateInputCustomValue, trigger: "change" }
				]
			}
		];
	}

	validateInputCustomValue(rule, value, callback) {
		if (value && (value < 1 || value > 50)) {
			const minValue = 1;
			const maxValue = 50;
			callback(
				fmtMsg(ContentExposureLocale.ContentExposureAddExposureMinMaxRequiredNumberofExposure, {
					minValue,
					maxValue
				})
			);
			return;
		}
		callback();
	}
	onAddCancel() {
		this.resetModal();
	}

	onAddExposure() {
		this.isAddVisible = true;
	}

	onExposureOk() {
		if (this.currentOperationType === AddDuplicateType.Add) {
			this.onAddExposureOk();
		} else {
			this.onDuplicateExposure();
		}
	}

	async onDuplicateExposure() {
		const currentMethodologyExposureIds = this.contentExposures.map((c) => (c as ContentExposureModel).id);

		if (currentMethodologyExposureIds.length === 0) {
			MessageHelper.Message(
				NotificationType.Warning,
				this.formatMessage(this.contentExposureLocale.ContentExposureDuplicateExposureWarning)
			);
			return;
		}

		this.isAddVisible = false;
		await this.onCopyExposure(currentMethodologyExposureIds, true);
		this.resetModal();
	}

	resetModal() {
		this.resetForm();
		this.isAddVisible = false;
		this.currentOperationType = AddDuplicateType.Add;
		this.duplicateTargetLPVId = this.targetDuplicateLPVs[0].id;
	}

	resetForm() {
		if (this.currentOperationType === AddDuplicateType.Add) {
			this.commonExposureAddForm.resetFields();
			this.currentSequence = this.contentExposures.length + 1;
			this.exposureToAdd = 1;
		} else {
			this.commonExposureDuplicateForm.resetFields();
		}
	}

	onOperationTypeChange() {
		this.resetForm();
	}

	onRadioMediaSlotChange(value) {
		ContentExposureModule.setMediaSlot(value);
	}

	async onAddExposureOk() {
		this.isAddVisible = false;
		this.loading = true;
		const lessonPlanVersionId = this.selectedLPV.id;
		const currentSequence = this.currentSequence === this.contentExposures.length + 1 ? 0 : this.currentSequence; // doing to add at last as copy api internally using create exposure function
		const numberOfExposures = this.exposureToAdd;
		const currentMediaSlot =
			this.selectedLPV.methodology === MethodologyTypeEnum.Digital ||
			this.selectedLPV.methodology === MethodologyTypeEnum.Remote
				? ContentExposureModule.defaultMediaSlot
				: null;
		const isDigitalOrRemoteLP =
			this.selectedLPV.methodology === MethodologyTypeEnum.Digital ||
			this.selectedLPV.methodology === MethodologyTypeEnum.Remote
				? true
				: false;
		const records = (await ContentExposureModule.addExposure({
			lessonPlanVersionId,
			currentSequence,
			numberOfExposures,
			currentMediaSlot,
			isDigitalOrRemoteLP
		}).catch((_) => Promise.reject((this.loading = false)))) as any;
		this.loading = false;
		this.resetModal();
		//this.recordInEditMode = record.id;

		if (records.length > 0) {
			const refElement = this.$refs["duration" + records[0].id] as any;

			let scrollValue = null;
			if (!isUndefined(refElement) && !isNull(refElement)) {
				if (!isUndefined(refElement.$el) && !isNull(refElement.$el)) {
					if (
						!isUndefined(refElement.$el.getBoundingClientRect) &&
						!isNull(refElement.$el.getBoundingClientRect)
					) {
						scrollValue = refElement.$el.getBoundingClientRect().top;
						if (refElement.$parent && refElement.$parent.$el) {
							const elementParentHeight = refElement.$parent.$el.clientHeight;
							if (
								!isUndefined(scrollValue) &&
								!isNull(scrollValue) &&
								isNumber(scrollValue) &&
								!isUndefined(elementParentHeight) &&
								!isNull(elementParentHeight) &&
								isNumber(elementParentHeight)
							) {
								const calculatedHeight = scrollValue - elementParentHeight;
								if (calculatedHeight >= 0) {
									scrollValue = calculatedHeight as any;
								}
							}
						}
					}
				}
			}
			//ContentExposureModule.editingExposure({ ...record, isEdit: false } as any);
			this.scrollBottom(scrollValue);
		}
	}

	exposureHasErrors() {
		return Object.keys(this.$refs).some((key) => (this.$refs[key] as any)?.showError);
	}

	async onSaveExposure(record) {
		if (this.exposureHasErrors()) {
			return;
		}

		this.loading = true;
		const isDigitalOrRemoteLP =
			this.selectedLPV.methodology === MethodologyTypeEnum.Digital ||
			this.selectedLPV.methodology === MethodologyTypeEnum.Remote
				? true
				: false;
		await ContentExposureModule.updateExposure({ record, isDigitalOrRemoteLP }).catch((_) => {
			MessageHelper.Message(NotificationType.Failed, fmtMsg(CommonLocale.CommonHttpServerError));
			this.loading = false;
		});
		this.recordInEditMode = null;
		this.loading = false;
	}

	async onEditExposure(record, isEdit) {
		if (this.disabledDueToAccessLevel) {
			return;
		}
		if (this.recordInEditMode && this.recordInEditMode !== record.id) {
			return;
		}
		if (isEdit) {
			this.recordInEditMode = record.id;
			record.isEdit = isEdit;
			ContentExposureModule.editingExposure(record);
		} else {
			this.recordInEditMode = null;
			this.loadContentExposures();
		}
	}

	async loadContentExposures() {
		this.exposureLoading = true;
		await ContentExposureModule.fetchContentExposures(this.selectedLPV.id);
		this.exposureLoading = false;
	}

	async onCopyExposure(recordIds: any[], isBulk: boolean) {
		this.loading = true;
		let targetLessonPlanVersionId = this.duplicateTargetLPVId;

		if (!isBulk) {
			targetLessonPlanVersionId = this.selectedLPV.id;
		}

		const lessonPlanVersionId = this.selectedLPV.id;

		const result = await ContentExposureModule.copyExposure({
			recordIds,
			targetLessonPlanVersionId,
			lessonPlanVersionId
		}).catch((_) => Promise.reject((this.loading = false)));

		this.loading = false;

		if (result.length > 0) {
			if (!isBulk) {
				ContentExposureModule.editingExposure({ ...result[0], isEdit: true } as any);
				this.recordInEditMode = result[0].id;
				this.scrollBottom();
			}
		}
	}

	onDeleteExposure(record) {
		Modal.confirm({
			title: fmtMsg(ContentExposureLocale.ContentExposureWarningDelete),
			content: "",
			onOk: async () => {
				this.loading = true;
				await ContentExposureModule.deleteExposure(record).catch((_) => (this.loading = false));
				this.loading = false;
			},
			onCancel: () => {}
		});
	}

	scrollBottom(value?) {
		this.$nextTick(() => {
			const dom = document.querySelector("main.vc-layout__content.ant-layout-content") as HTMLDivElement;
			if (dom) {
				dom.style.scrollBehavior = "smooth";
				dom.scrollTop = !isUndefined(value) && !isNull(value) ? value : dom.scrollHeight;
				dom.style.scrollBehavior = "inherit";
			}
		});
	}

	onExpSubmit(record: any, values: TeachingActivityModel[]) {
		const contentExposureTeachingActivity = record.contentExposureTeachingActivity.map((activity) => {
			const teachingActivity = values.find((item) => item.id === activity.teachingActivityId);

			if (teachingActivity) {
				const { pageId, imageName, metaData, objectiveId } = teachingActivity;
				return { ...activity, pageId, imageName, metaData, objectiveId };
			} else {
				return { ...activity };
			}
		});

		ContentExposureModule.setTeachingActivity({ record, values });
		ContentExposureModule.setExposureTeachingActivity({ record, values: contentExposureTeachingActivity });
	}

	onSaveAssociatedTAObjectives(record: any, values) {
		const { selectedObjectiveIds } = values;
		let { teachingActivities } = values;
		// remove association of objectives not present in list selectedObjectiveIds from Teaching Activities
		const contentExposureTeachingActivity = record.contentExposureTeachingActivity.map((activity) => {
			const teachingActivity = teachingActivities.find((item) => item.id === activity.teachingActivityId);

			if (teachingActivity) {
				const { pageId, imageName, metaData } = teachingActivity;
				let { objectiveId } = teachingActivity;
				if (objectiveId && selectedObjectiveIds.indexOf(objectiveId) < 0) {
					// update in teachingActivities
					teachingActivities = teachingActivities.map((ta) => {
						if (ta.objectiveId === objectiveId) {
							ta.objectiveId = null;
						}
						return ta;
					});

					objectiveId = null;
				}
				return { ...activity, pageId, imageName, metaData, objectiveId };
			} else {
				return { ...activity };
			}
		});
		ContentExposureModule.setTeachingActivity({ record, values: teachingActivities });
		ContentExposureModule.setExposureTeachingActivity({ record, values: contentExposureTeachingActivity });
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	handleLPVChange(lpvId: string) {
		this.selectedLPVId = lpvId;
		this.selectedLPV = this.findLessonPlanVersionById(lpvId);
	}

	findLessonPlanVersionById(lpvId: string): ILessonPlanVersionSimpleModel {
		return this.lessonPlanVersions.find((lpv) => lpv.id === lpvId) || ({} as ILessonPlanVersionSimpleModel);
	}

	handleLPVSearch(value: any) {
		this.selectedLPVId = "";
		this.filteredLPVs = this.lessonPlanVersions.filter((lpv: any) => {
			const combinedNameMethodology = lpv.combinedNameMethodology.toLowerCase();
			return combinedNameMethodology.includes(value.toLowerCase());
		});
	}

	get methodology(): string {
		if (this.selectedLPV?.methodology === MethodologyType.Classic) {
			return MethodologyTypeItems.get(MethodologyType.Classic) || Methodology.Classic;
		} else if (this.selectedLPV?.methodology === MethodologyType.Digital) {
			return MethodologyTypeItems.get(MethodologyType.Digital) || Methodology.Digital;
		} else if (this.selectedLPV?.methodology === MethodologyType.Remote) {
			return MethodologyTypeItems.get(MethodologyType.Remote) || Methodology.Remote;
		} else {
			return MethodologyTypeItems.get(MethodologyType.Independent) || Methodology.Independent;
		}
	}

	handleChangeduplicateTargetLPV(lpvId: string) {
		this.duplicateTargetLPVId = lpvId;
	}

	getMethodologyName(methodology: number) {
		return MethodologyTypeItems.get(methodology);
	}

	onManageOrder(currentExposure) {
		this.currentSelectedExposureId = currentExposure.id;
		this.manageOrderDrawerVisible = true;
	}

	onManageDrawerClose() {
		this.manageOrderDrawerVisible = false;
		this.currentSelectedExposureId = null;
	}

	get isReader() {
		const { editedContentDetails } = this.titleListModule;
		return editedContentDetails.languageSkillId === LanguageSkill.Read;
	}
}
